import React, { useState, useEffect } from 'react';
import s from './PersonalCapsuleItem.module.css';
import { LayerModal } from '../PersonalCapsuleItem/layerModal';
import { useParams } from "react-router-dom";
import { assistantAxiosInstance, pythonBackAxiosInstance } from "../../../api";
import CollageSchemes from './CollageSchemes';
import { IStyleFromPython } from "../../../types";
import { toast } from "react-toastify";

export type Layer =
    | 'ABOVE_THE_WAIST_FIRST_LAYER'
    | 'ABOVE_THE_WAIST_SECOND_LAYER'
    | 'OUTERWEAR'
    | 'PANTS'
    | 'SHOES'
    | 'BAG'
    | 'ACCESSORIES';

export interface CapsuleItem {
    id: number;
    name: string;
    brand: string;
    image?: string;
    layer?: Layer;
    source?: 'closet' | 'wishlist';
    style?: string;
    colorHex?: string;
    thumbUrl?: string;
    photoUrl?: string;
    cost?: number;
    manufacturer?: string;
}

interface Capsule {
    id: number;
    name: string;
    items: CapsuleItem[];
    fashionGuid?: string;
    schemaId?: number | null;
    styleId?: number | null;
    collage?: string | null;
    totalCost?: number;
    styleName?: string;
    dateCreated?: string;
    fashionComposition?: FashionComposition[];
}

interface FashionComposition {
    id: number;
    name: string;
    category: string;
    thumbUrl: string;
    cost: number;
    manufacturer: string;
    variantId: number | null;
    clothLayer: string;
    isFavorite: boolean | null;
    isWardrobe: boolean | null;
    colorCloth: string | null;
    isBasisOfCollage: boolean;
}

interface DetailsCollageInfo {
    collage: string | null;
    event: string | null;
    styleName: string;
    styleId: number | null;
    fittingPhotoUrl: string | null;
    totalCost: number;
    fashionGuid: string;
    isFavorite: boolean | null;
    commentStylist: string | null;
    adviceStylist: string | null;
    fashionComposition: FashionComposition[];
    status: string;
    schemaId?: number | null;
    dateCreated?: string;
}

interface HistoryResponse {
    requestId: string;
    dateCreated: string;
    userId: number;
    isPublished: boolean;
    viewedDate: string | null;
    commentStylist: string | null;
    collages: DetailsCollageInfo[];
}

interface FashionCompositionModalProps {
    composition: FashionComposition[];
    onClose: () => void;
    totalCost: number;
    onOpenItemPage: (item: FashionComposition) => void;
}

const FashionCompositionModal: React.FC<FashionCompositionModalProps> = ({
                                                                             composition,
                                                                             onClose,
                                                                             totalCost,
                                                                             onOpenItemPage
                                                                         }) => {
    return (
        <div className={s.modalOverlay} onClick={onClose}>
            <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={s.modalHeader}>
                    <div>
                        <h3>Состав образа</h3>
                        <p className={s.totalCost}>Общая стоимость: {totalCost} ₽</p>
                    </div>
                    <button className={s.closeButton} onClick={onClose}>✕</button>
                </div>
                <div className={s.compositionList}>
                    {composition.map((item, index) => (
                        <div className={s.compositionItem} key={index}>
                            <div className={s.compositionInfoBlock}>
                                <img
                                    src={item.thumbUrl}
                                    alt={item.name}
                                    className={s.compositionImage}
                                />
                                <div className={s.compositionInfo}>
                                    <h4>{item.name}</h4>
                                    <p>{item.manufacturer}</p>
                                    <p><strong>{item.cost} ₽</strong></p>
                                </div>
                            </div>
                            <button
                                className={s.pageClothBtn}
                                onClick={() => onOpenItemPage(item)}
                                disabled={!item || !item.id}
                            >
                                Перейти на страницу товара
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const LAYER_LABELS: Record<Layer, string> = {
    'ABOVE_THE_WAIST_FIRST_LAYER': 'Плечевой первый слой',
    'ABOVE_THE_WAIST_SECOND_LAYER': 'Плечевой второй слой',
    'OUTERWEAR': 'Плечевые верхняя одежда',
    'PANTS': 'Поясные',
    'SHOES': 'Обувь',
    'BAG': 'Сумки',
    'ACCESSORIES': 'Аксессуары',
};

const AVAILABLE_LAYERS: Layer[] = [
    'ABOVE_THE_WAIST_FIRST_LAYER',
    'ABOVE_THE_WAIST_SECOND_LAYER',
    'OUTERWEAR',
    'PANTS',
    'SHOES',
    'BAG',
    'ACCESSORIES'
];

interface PersonalCapsuleItemProps {
    gender?: string;
    userId?: number;
    requestId?: string;
}

const PersonalCapsuleItem: React.FC<PersonalCapsuleItemProps> = ({
                                                                     gender: genderProp
                                                                 }) => {
    const [capsules, setCapsules] = useState<Capsule[]>([]);
    const [historyGroups, setHistoryGroups] = useState<HistoryResponse[]>([]);
    const { userId } = useParams<{ userId: string }>();
    const userIdNum = userId ? Number(userId) : undefined;
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
    const [editingCapsuleIndex, setEditingCapsuleIndex] = useState<number | null>(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLayer, setSelectedLayer] = useState<Layer | null>(null);
    const [selectedItem, setSelectedItem] = useState<CapsuleItem | null>(null);

    const [currentComment, setCurrentComment] = useState<string>('');
    const [capsuleSchemaIds, setCapsuleSchemaIds] = useState<(number | null)[]>([]);
    const [capsuleStyleIds, setCapsuleStyleIds] = useState<(number | null)[]>([]);

    const [showSchemesBlock, setShowSchemesBlock] = useState(false);
    const [availableAllStyles, setAvailableAllStyles] = useState<IStyleFromPython[]>([]);
    const [isStyleDropdownOpen, setIsStyleDropdownOpen] = useState<number | null>(null);
    const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [currentIsPublished, setCurrentIsPublished] = useState<boolean>(false);
    const [currentViewedDate, setCurrentViewedDate] = useState<string | null>(null);
    const [selectedComposition, setSelectedComposition] = useState<FashionComposition[] | null>(null);
    const [selectedCollage, setSelectedCollage] = useState<Capsule | null>(null);

    const gender = genderProp || localStorage.getItem('userGender') || '';

    const openCompositionModal = (capsule: Capsule) => {
        if (capsule.fashionComposition && capsule.fashionComposition.length > 0) {
            setSelectedCollage(capsule);
            setSelectedComposition(capsule.fashionComposition);
        } else {
            toast.info('Нет состава для этого коллажа');
        }
    };

    const closeCompositionModal = () => {
        setSelectedCollage(null);
        setSelectedComposition(null);
    };

    const handleOpenItemPage = (item: FashionComposition) => {
        const adminBaseUrl = process.env.REACT_APP_STYLIST_ADMIN_PANEL_URL || '';
        const link = `${adminBaseUrl}${item.id}`;
        if (link && link !== '#') {
            window.open(link, '_blank', 'noopener,noreferrer');
        } else {
            toast.error('Ссылка на товар недоступна');
        }
    };

    useEffect(() => {
        if (capsules.length > 0) {
            setCapsuleSchemaIds(prev => {
                if (prev.length === 0) {
                    return new Array(capsules.length).fill(null);
                }
                return prev;
            });
            setCapsuleStyleIds(prev => {
                if (prev.length === 0) {
                    return new Array(capsules.length).fill(null);
                }
                return prev;
            });
        }
    }, [capsules.length]);

    useEffect(() => {
        if (userIdNum) {
            loadCapsuleData(userIdNum);
        } else {
            setLoading(false);
        }
    }, [userIdNum]);

    const loadCapsuleData = async (userId: number) => {
        setLoading(true);
        try {
            const response = await assistantAxiosInstance.get<HistoryResponse[]>(
                `mobile/fashion/adminHistoryPersonalCapsule/${userId}`
            );

            const data = response.data;

            if (data && data.length > 0) {
                const firstGroup = data[0];
                const collages = firstGroup.collages || [];

                setCurrentRequestId(firstGroup.requestId);
                setCurrentIsPublished(firstGroup.isPublished || false);
                setCurrentComment(firstGroup.commentStylist || '');
                setCurrentViewedDate(firstGroup.viewedDate ?? null);

                const newCapsules: Capsule[] = collages.map((item, index) => {
                    const items: CapsuleItem[] = item.fashionComposition.map((comp) => ({
                        id: comp.id,
                        name: comp.name,
                        brand: comp.manufacturer || '',
                        image: comp.thumbUrl,
                        thumbUrl: comp.thumbUrl,
                        layer: comp.clothLayer as Layer,
                        cost: comp.cost,
                        manufacturer: comp.manufacturer,
                    }));

                    return {
                        id: index + 1,
                        name: `Коллаж ${index + 1}`,
                        items: items,
                        fashionGuid: item.fashionGuid,
                        schemaId: item.schemaId ?? null,
                        styleId: item.styleId ?? null,
                        collage: item.collage,
                        totalCost: item.totalCost,
                        styleName: item.styleName,
                        dateCreated: firstGroup.dateCreated,
                        fashionComposition: item.fashionComposition,
                    };
                });

                setCapsules(newCapsules);
                setIsCreatingNew(false);
                setHasUnsavedChanges(false);

                const schemaIds = collages.map(item => item.schemaId ?? null);
                setCapsuleSchemaIds(schemaIds);

                const styleIds = collages.map(item => item.styleId ?? null);
                setCapsuleStyleIds(styleIds);

                setHistoryGroups(data);
            } else {
                setIsCreatingNew(true);
                setHasUnsavedChanges(false);
                setCurrentIsPublished(false);
                setCurrentComment('');
                setCurrentViewedDate(null);

                const emptyCapsules: Capsule[] = [{
                    id: 1,
                    name: 'Коллаж 1',
                    items: [],
                    styleId: null,
                    schemaId: null,
                }];
                setCapsules(emptyCapsules);
                setCapsuleSchemaIds([null]);
                setCapsuleStyleIds([null]);
                setCurrentRequestId(null);
                setHistoryGroups([]);
            }
        } catch (error) {
            setCapsules([]);
            setHistoryGroups([]);
            setCurrentRequestId(null);
            setCurrentIsPublished(false);
            setCurrentComment('');
            setCurrentViewedDate(null);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNewSelection = () => {
        const hasItems = capsules.some(c => c.items.length > 0);

        if (hasItems && !isCreatingNew) {
            const currentCapsuleHistory: HistoryResponse = {
                requestId: currentRequestId || '',
                dateCreated: new Date().toISOString(),
                userId: userIdNum || 0,
                isPublished: currentIsPublished,
                viewedDate: currentViewedDate,
                commentStylist: currentComment,
                collages: capsules.map(capsule => ({
                    collage: capsule.collage || null,
                    event: null,
                    styleName: capsule.styleName || '',
                    styleId: capsule.styleId || null,
                    fittingPhotoUrl: null,
                    totalCost: capsule.totalCost || 0,
                    fashionGuid: capsule.fashionGuid || '',
                    isFavorite: null,
                    commentStylist: null,
                    adviceStylist: null,
                    fashionComposition: capsule.fashionComposition || [],
                    status: 'SUCCESS',
                    schemaId: capsule.schemaId || null,
                    dateCreated: capsule.dateCreated || new Date().toISOString()
                }))
            };

            setHistoryGroups(prev => [currentCapsuleHistory, ...prev]);
        }

        setIsCreatingNew(true);
        setCurrentRequestId(null);
        setCurrentIsPublished(false);
        setCurrentComment('');
        setCurrentViewedDate(null);
        setHasUnsavedChanges(false);

        const emptyCapsules: Capsule[] = [{
            id: 1,
            name: 'Коллаж 1',
            items: [],
            styleId: null,
            schemaId: null,
        }];

        setCapsules(emptyCapsules);
        setCapsuleSchemaIds([null]);
        setCapsuleStyleIds([null]);

        toast.info('Создана новая подборка. Добавьте вещи и сохраните');
    };

    const handleAddCapsule = () => {
        const newCapsule: Capsule = {
            id: capsules.length + 1,
            name: `Коллаж ${capsules.length + 1}`,
            items: [],
            styleId: null,
            schemaId: null,
        };
        setCapsules([...capsules, newCapsule]);
        setCapsuleSchemaIds([...capsuleSchemaIds, null]);
        setCapsuleStyleIds([...capsuleStyleIds, null]);
        setHasUnsavedChanges(true);
    };

    const handleDeleteItem = (capsuleIndex: number, itemIndex: number) => {
        setCapsules(prev => {
            const updated = [...prev];
            updated[capsuleIndex] = {
                ...updated[capsuleIndex],
                items: updated[capsuleIndex].items.filter((_, idx) => idx !== itemIndex)
            };
            return updated;
        });
        setHasUnsavedChanges(true);
    };

    const handleEditCapsule = (index: number) => {
        setEditingCapsuleIndex(index);

        setCapsules(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                collage: null
            };
            return updated;
        });
        setHasUnsavedChanges(true);
    };

    const handleOpenModal = (index: number) => {
        setEditingCapsuleIndex(index);
        setSelectedLayer(null);
        setSelectedItem(null);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setEditingCapsuleIndex(null);
        setSelectedLayer(null);
        setSelectedItem(null);
    };

    const handleLayerSelect = (layer: Layer | null) => {
        setSelectedLayer(layer);
    };

    const handleItemSelect = (item: CapsuleItem | null) => {
        setSelectedItem(item);
    };

    const handleConfirm = () => {
        if (selectedLayer && selectedItem && editingCapsuleIndex !== null) {
            const newItem: CapsuleItem = {
                id: selectedItem.id,
                name: selectedItem.name,
                brand: selectedItem.brand || '',
                image: selectedItem.image || selectedItem.photoUrl,
                layer: selectedLayer,
                source: selectedItem.source,
                thumbUrl: selectedItem.thumbUrl,
                photoUrl: selectedItem.photoUrl,
                cost: selectedItem.cost,
                manufacturer: selectedItem.manufacturer,
            };

            setCapsules(prev => {
                const updated = [...prev];
                updated[editingCapsuleIndex] = {
                    ...updated[editingCapsuleIndex],
                    items: [...updated[editingCapsuleIndex].items, newItem]
                };
                return updated;
            });
            setHasUnsavedChanges(true);
            handleModalClose();
        }
    };

    const getStyles = async () => {
        try {
            const response = await pythonBackAxiosInstance.get(`/v1/styles`, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const filteredStyles = response.data.filter((style: IStyleFromPython) => {
                if (style.gender === 'unisex') return true;
                if (style.gender === null) return true;
                return style.gender === gender;
            });

            setAvailableAllStyles(filteredStyles);
        } catch (error) {
            toast.error('Ошибка при получении списка стилей');
            setAvailableAllStyles([]);
        }
    };

    useEffect(() => {
        getStyles();
    }, [gender]);

    const handleCommentChange = (comment: string) => {
        setCurrentComment(comment);
        setHasUnsavedChanges(true);
    };

    const handleSchemaChange = (index: number, schemaId: number | null) => {
        setCapsuleSchemaIds(prev => {
            const updated = [...prev];
            updated[index] = schemaId;
            return updated;
        });
        setHasUnsavedChanges(true);
    };

    const handleStyleSelect = (capsuleIndex: number, styleId: number | null) => {
        setCapsuleStyleIds(prev => {
            const updated = [...prev];
            updated[capsuleIndex] = styleId;
            return updated;
        });

        setCapsules(prev => {
            const updated = [...prev];
            updated[capsuleIndex] = {
                ...updated[capsuleIndex],
                styleId: styleId
            };
            return updated;
        });
        setHasUnsavedChanges(true);
        setIsStyleDropdownOpen(null);
    };

    const toggleStyleDropdown = (index: number) => {
        setIsStyleDropdownOpen(prev => prev === index ? null : index);
    };

    const getStyleNameById = (styleId: number | null): string => {
        if (!styleId) return 'Выберите стиль';
        const style = availableAllStyles.find(s => s.id === styleId);
        return style ? style.name : 'Выберите стиль';
    };

    const toggleSchemesBlock = () => {
        setShowSchemesBlock(!showSchemesBlock);
    };

    const toggleHistoryGroup = (requestId: string) => {
        setExpandedHistory(prev => prev === requestId ? null : requestId);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatViewedDate = (dateString: string | null) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const getTotalItemsCount = () => {
        return capsules.reduce((sum, capsule) => sum + capsule.items.length, 0);
    };

    const handleSave = async () => {
        if (!userIdNum) {
            setSaveError('Пользователь не найден');
            return;
        }

        const capsulesWithItems = capsules.filter(capsule => capsule.items.length > 0);

        for (let i = 0; i < capsulesWithItems.length; i++) {
            const capsule = capsulesWithItems[i];
            const originalIndex = capsules.indexOf(capsule);

            if (!capsuleStyleIds[originalIndex]) {
                toast.warning(`У коллажа ${originalIndex + 1} отсутствует стиль`);
                return;
            }
        }

        const needGenerate = isCreatingNew || hasUnsavedChanges;

        const collages = capsules
            .filter(capsule => capsule.items.length > 0)
            .map((capsule, index) => ({
                fashionGuid: capsule.fashionGuid || null,
                ids: capsule.items.map(item => item.id),
                schemaId: capsuleSchemaIds[index] || null,
                styleId: capsuleStyleIds[index] || null,
                needGenerateImageCollage: needGenerate
            }));

        if (collages.length === 0) {
            toast.warning('Нет вещей для сохранения');
            return;
        }

        setIsSaving(true);
        setSaveError(null);

        try {
            const payload: any = {
                collages: collages,
                user: userIdNum,
                commentStylist: currentComment
            };

            if (!isCreatingNew && currentRequestId && currentRequestId.trim() !== '') {
                payload.requestId = currentRequestId;
            }

            await assistantAxiosInstance.post(
                'mobile/assistant/createPersonalCapsule',
                payload
            );

            toast.success('Коллажи успешно сохранены!');

            await loadCapsuleData(userIdNum);
            setIsCreatingNew(false);
            setCurrentRequestId(null);
            setHasUnsavedChanges(false);

            window.location.reload();

        } catch (err) {
            setSaveError('Ошибка при сохранении коллажей');
            toast.error('Ошибка при сохранении коллажей');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePublish = async () => {
        if (!currentRequestId) {
            toast.warning('Сначала сохраните коллажи');
            return;
        }
        try {
            const response = await assistantAxiosInstance.post(
                'mobile/assistant/publishPersonalCapsule',
                currentRequestId,
                { headers: { 'Content-Type': 'application/json' } }
            );

            setCurrentIsPublished(true);

            if (response.data.pushSent === false) {
                toast.warning('Ошибка отправки пуша');
            } else {
                toast.success('Капсула опубликована!');
            }

            if (userIdNum) await loadCapsuleData(userIdNum);
        } catch (error) {
            toast.error('Ошибка при публикации');
        }
    };

    const handleUnpublish = async () => {
        if (!currentRequestId) {
            toast.warning('Сначала сохраните коллажи');
            return;
        }
        try {
            await assistantAxiosInstance.post(
                'mobile/assistant/unPublishPersonalCapsule',
                currentRequestId,
                { headers: { 'Content-Type': 'application/json' } }
            );
            setCurrentIsPublished(false);
            toast.success('Публикация снята');
            if (userIdNum) await loadCapsuleData(userIdNum);
        } catch (error) {
            toast.error('Ошибка при снятии публикации');
        }
    };

    const handleTogglePublish = () => {
        if (currentIsPublished) {
            handleUnpublish();
        } else {
            handlePublish();
        }
    };

    if (loading) return <div className={s.loading}>Загрузка...</div>;

    return (
        <div className={s.container}>
            <div className={s.header}>
                <h2 className={s.title}>Коллажи</h2>
                <div className={s.headerActions}>
                    {hasUnsavedChanges && (
                        <span className={s.unsavedIndicator}>● Несохраненные изменения</span>
                    )}

                    <span className={`${s.publishBadge} ${currentIsPublished ? s.publishBadgeActive : s.publishBadgeInactive}`}>
                        {currentIsPublished ? 'Опубликован' : 'Не опубликован'}
                    </span>

                    <span className={`${s.viewedBadge} ${currentViewedDate ? s.viewedBadgeActive : s.viewedBadgeInactive}`}>
                        {currentViewedDate
                            ? `Просмотрено ${formatViewedDate(currentViewedDate)}`
                            : 'Не просмотрено'}
                    </span>

                    <button
                        className={currentIsPublished ? s.unpublishButton : s.publishButton}
                        onClick={handleTogglePublish}
                        disabled={!currentRequestId}
                    >
                        {currentIsPublished ? 'Снять с публикации' : 'Опубликовать'}
                    </button>

                    <button
                        className={s.createNewButton}
                        onClick={handleCreateNewSelection}
                        disabled={isCreatingNew}
                    >
                        {isCreatingNew ? 'Новая подборка' : '+ Создать новую подборку'}
                    </button>
                    <button
                        className={s.schemesToggleButton}
                        onClick={toggleSchemesBlock}
                    >
                        {showSchemesBlock ? 'Скрыть схемы' : 'Показать схемы'}
                    </button>
                    <button
                        className={s.saveButton}
                        onClick={handleSave}
                        disabled={isSaving || getTotalItemsCount() === 0}
                    >
                        {isSaving ? 'Сохранение...' : 'Сохранить'}
                    </button>

                </div>
            </div>

            {saveError && (
                <div className={s.saveError}>{saveError}</div>
            )}

            <div className={s.capsulesSlider}>

                {capsules.map((capsule, index) => (
                    <div key={capsule.id} className={s.capsuleItem}>
                        <button
                            className={s.addButton}
                            onClick={() => handleOpenModal(index)}
                        >
                            + Добавить вещь
                        </button>

                        <div className={s.capsuleImageContainer}>
                            {capsule.collage ? (
                                <>
                                    <img
                                        src={capsule.collage}
                                        alt={capsule.name}
                                        className={s.capsuleImage}
                                    />
                                    <button
                                        className={s.editButton}
                                        onClick={() => handleEditCapsule(index)}
                                    >
                                        Редактировать
                                    </button>
                                </>
                            ) : (
                                <div className={s.capsuleImage} />
                            )}

                            {capsule.items.length > 0 && !capsule.collage && (
                                <div className={s.capsuleItemsOverlay}>
                                    <div className={s.capsuleItemsGrid}>
                                        {capsule.items.map((item, itemIndex) => (
                                            <div key={itemIndex} className={s.capsuleItemOverlay}>
                                                <img
                                                    src={item.image || '/placeholder.svg'}
                                                    alt={item.name}
                                                    className={s.capsuleItemOverlayImage}
                                                />
                                                <button
                                                    className={s.capsuleItemOverlayRemove}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteItem(index, itemIndex);
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={s.capsuleInfo}>
                            <span className={s.capsuleItemsCount}>
                                {capsule.items.length} вещей
                            </span>
                            {capsuleSchemaIds[index] && (
                                <span className={s.schemeBadge}>
                                    Схема выбрана
                                </span>
                            )}
                        </div>

                        <div className={s.capsuleActions}>
                            {capsule.fashionComposition && capsule.fashionComposition.length > 0 && (
                                <button
                                    className={s.compositionButton}
                                    onClick={() => openCompositionModal(capsule)}
                                >
                                    Состав образа
                                </button>
                            )}
                        </div>

                        <div className={s.styleSelectorSection}>
                            <button
                                className={s.styleSelectorButton}
                                onClick={() => toggleStyleDropdown(index)}
                            >
                                {capsuleStyleIds[index] ? (
                                    <span className={s.styleSelectorText}>
                                        <span className={s.styleSelectorLabel}>Стиль:</span>
                                        {getStyleNameById(capsuleStyleIds[index])}
                                    </span>
                                ) : (
                                    <span className={s.styleSelectorPlaceholder}>
                                        Выберите стиль
                                    </span>
                                )}
                                <span className={s.styleSelectorArrow}>▼</span>
                            </button>

                            {isStyleDropdownOpen === index && (
                                <div className={s.styleDropdown}>
                                    <div
                                        className={s.styleOption}
                                        onClick={() => handleStyleSelect(index, null)}
                                    >
                                        Очистить
                                    </div>
                                    {availableAllStyles.length > 0 ? (
                                        availableAllStyles.map((style) => (
                                            <div
                                                key={style.id}
                                                className={`${s.styleOption} ${capsuleStyleIds[index] === style.id ? s.styleOptionSelected : ''}`}
                                                onClick={() => handleStyleSelect(index, style.id)}
                                            >
                                                {style.name}
                                            </div>
                                        ))
                                    ) : (
                                        <div className={s.noStylesMessage}>Нет доступных стилей</div>
                                    )}
                                </div>
                            )}
                        </div>


                    </div>
                ))}

                <div className={s.addCapsuleCard}>
                    <button
                        className={s.addCapsuleButton}
                        onClick={handleAddCapsule}
                    >
                        <span className={s.addCapsuleIcon}>+</span>
                        <span>Добавить коллаж</span>
                    </button>
                </div>
            </div>

            <div className={s.commentSection}>
                <textarea
                    className={s.commentTextarea}
                    value={currentComment}
                    onChange={(e) => handleCommentChange(e.target.value)}
                    placeholder="Комментарий стилиста..."
                    rows={2}
                />
            </div>

            {showSchemesBlock && (
                <div className={s.schemesBlock}>
                    <div className={s.schemesHeader}>
                        <h3 className={s.schemesTitle}>Схемы коллажей</h3>
                        <span className={s.schemesHint}>Выберите схему для каждого коллажа</span>
                    </div>
                    <CollageSchemes
                        gender={gender || ''}
                        styleName="Коллажи"
                        requestId={currentRequestId || ''}
                        savedSchemaIds={capsuleSchemaIds}
                        onSchemeChange={handleSchemaChange}
                        capsuleCount={capsules.length}
                    />
                </div>
            )}

            {historyGroups.length > 1 && (
                <div className={s.historySection}>
                    <h3 className={s.historyTitle}>История коллажей</h3>
                    <div className={s.historyList}>
                        {historyGroups.slice(1).map((group) => (
                            <div key={group.requestId} className={s.historyGroup}>
                                <button
                                    className={s.historyGroupHeader}
                                    onClick={() => toggleHistoryGroup(group.requestId)}
                                >
                                    <span className={s.historyGroupDate}>
                                        {formatDate(group.dateCreated)}
                                    </span>

                                    <span className={`${s.historyPublishBadge} ${group.isPublished ? s.publishBadgeActive : s.publishBadgeInactive}`}>
                                        {group.isPublished ? 'Опубликован' : 'Не опубликован'}
                                    </span>

                                    <span className={`${s.viewedBadge} ${group.viewedDate ? s.viewedBadgeActive : s.viewedBadgeInactive}`}>
                                        {group.viewedDate
                                            ? `Просмотрено ${formatViewedDate(group.viewedDate)}`
                                            : 'Не просмотрено'}
                                    </span>

                                    <span className={s.historyGroupCount}>
                                        {group.collages.length} коллажей
                                    </span>
                                    <span className={s.historyGroupArrow}>
                                        {expandedHistory === group.requestId ? '▼' : '▶'}
                                    </span>
                                </button>
                                {expandedHistory === group.requestId && (
                                    <div className={s.historyCollages}>
                                        {group.commentStylist && group.commentStylist.trim() !== '' && (
                                            <div className={s.historyGroupComment}>
                                                <span className={s.historyCommentText}>
                                                    {group.commentStylist}
                                                </span>
                                            </div>
                                        )}

                                        {group.collages.map((collage, idx) => (
                                            <div key={idx} className={s.historyCollageItem}>
                                                <div className={s.historyCollageImageWrapper}>
                                                    {collage.collage ? (
                                                        <img
                                                            src={collage.collage}
                                                            alt={`Коллаж ${idx + 1}`}
                                                            className={s.historyCollageImage}
                                                        />
                                                    ) : (
                                                        <div className={s.historyCollagePlaceholder}>
                                                            Коллаж {idx + 1}
                                                        </div>
                                                    )}
                                                    {collage.totalCost !== undefined && collage.totalCost > 0 && (
                                                        <div className={s.historyCostOverlay}>
                                                            <span className={s.historyCostValue}>
                                                                {collage.totalCost} ₽
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={s.historyCollageInfo}>
                                                    <span className={s.historyCollageName}>
                                                        Коллаж {idx + 1}
                                                    </span>
                                                    {collage.styleName && (
                                                        <span className={s.historyCollageStyle}>
                                                            {collage.styleName}
                                                        </span>
                                                    )}
                                                    <span className={s.historyCollageItems}>
                                                        {collage.fashionComposition.length} вещей
                                                    </span>

                                                    <button
                                                        className={s.historyCollageCompositionBtn}
                                                        onClick={() => {
                                                            const capsule: Capsule = {
                                                                id: idx + 1,
                                                                name: `Коллаж ${idx + 1}`,
                                                                items: [],
                                                                fashionComposition: collage.fashionComposition,
                                                                totalCost: collage.totalCost,
                                                                styleName: collage.styleName,
                                                            };
                                                            openCompositionModal(capsule);
                                                        }}
                                                    >
                                                        Состав образа
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <LayerModal
                open={isModalOpen}
                onOpenChange={handleModalClose}
                availableLayers={AVAILABLE_LAYERS}
                selectedLayer={selectedLayer}
                selectedItem={selectedItem}
                onLayerSelect={handleLayerSelect}
                onItemSelect={handleItemSelect}
                onConfirm={handleConfirm}
                layerLabels={LAYER_LABELS}
                gender={gender}
                userId={userIdNum}
            />

            {selectedComposition && selectedCollage && (
                <FashionCompositionModal
                    composition={selectedComposition}
                    onClose={closeCompositionModal}
                    totalCost={selectedCollage.totalCost || 0}
                    onOpenItemPage={handleOpenItemPage}
                />
            )}
        </div>
    );
};

export default PersonalCapsuleItem;